import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FILE_STATUS, ROUTE_NAMES, TOAST_VARIANT } from "@root/constants";
import { createDocument } from "@root/store/thunks/documentsActions";
import { getFileType as getFileTypeMain } from "@root/utils";
import { useFileSizeValidation } from "./useFileSizeValidation";
import { useFileValidation } from "@root/utils/fileValidationUtils";
import { useNotification } from "./useNotification";

interface UseFileHandlingProps {
  attachedFile: any[];
  setAttachedFile: (files: any[] | ((prev: any[]) => any[])) => void;
  displayFiles: any[];
  setDisplayFiles: (files: any[] | ((prev: any[]) => any[])) => void;
  isDrawingAnalysis: boolean;
  isLegalChatbot: boolean;
  fetchDocument?: (id: number) => void;
  setInput?: (value: string) => void;
  input?: string;
}

/**
 * Custom hook to handle file upload, validation, and processing
 */
export const useFileHandling = ({
  attachedFile,
  setAttachedFile,
  displayFiles,
  setDisplayFiles,
  isDrawingAnalysis,
  isLegalChatbot,
  fetchDocument,
  setInput,
  input,
}: UseFileHandlingProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const toast = useToast();
  const { t } = useTranslation("common");
  const { showNotification } = useNotification();
  const { validateFile } = useFileValidation();

  // File size validation hook
  const maxFileSizeMB = isDrawingAnalysis
    ? Number(process.env.REACT_APP_MATERIAL_FILE_LIMIT) || 500
    : Number(process.env.REACT_APP_GLOBAL_FILE_SIZE_LIMIT) || 300;
  const validateFiles = useFileSizeValidation(maxFileSizeMB);

  const [pendingFiles, setPendingFiles] = useState<any[]>([]);

  // Helper function to update both states
  const addFile = useCallback(
    (file: any) => {
      setAttachedFile((prev: any[]) => [...prev, file]);
      setDisplayFiles((prev: any[]) => [...prev, file]);
    },
    [setAttachedFile, setDisplayFiles]
  );

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files) return;

      // Process files sequentially to handle async PDF validation
      for (const file of Array.from(files)) {
        // Validate file type and PDF text-based
        const isValid = await validateFile(file, isDrawingAnalysis, isLegalChatbot);
        if (!isValid) {
          continue; // Skip invalid files
        }

        // Add to pending files until processing to show loading
        const tempFile = {
          id: Math.random().toString(),
          name: file.name,
          status: FILE_STATUS.PROCESSING,
        };
        setPendingFiles((prev) => [...prev, tempFile]);

        // Backend API call
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", String(getFileTypeMain(pathname)));
        formData.append("name", file.name);

        dispatch(
          createDocument({
            formData,
            onSuccess: (res: any) => {
              // API complete → remove from pendingFiles and add to attachedFile
              setPendingFiles((prev) => prev.filter((f) => f.id !== tempFile.id));
              // Update BOTH states
              addFile(res);
              if (isDrawingAnalysis && fetchDocument) {
                fetchDocument(res?.id);
              }
              if (pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD && setInput) {
                setInput(input || "");
                navigate(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT);
              }
            },
            onError: () => {
              // Error → remove from pendingFiles or set status error
              setPendingFiles((prev) =>
                prev.map((f) =>
                  f.id === tempFile.id ? { ...f, status: "error" } : f
                )
              );
              toast({
                title: t("upload_document.upload_failed"),
                status: "error",
              });
            },
          })
        );
      }
    },
    [
      validateFile,
      isDrawingAnalysis,
      isLegalChatbot,
      pathname,
      dispatch,
      addFile,
      fetchDocument,
      setInput,
      input,
      navigate,
      toast,
      t,
    ]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e?.target?.files) return;

      // Validate file size using hook
      const validFileList = validateFiles(e.target.files);
      if (!validFileList) return;

      await handleFiles(validFileList);
    },
    [validateFiles, handleFiles]
  );

  const removeAttachment = useCallback(
    (identifier: string) => {
      // Remove from BOTH states
      setAttachedFile((prev: any) =>
        prev.filter((file: any) => file.id !== Number(identifier))
      );
      setDisplayFiles((prev: any) =>
        prev.filter((file: any) => file.id !== Number(identifier))
      );
    },
    [setAttachedFile, setDisplayFiles]
  );

  return {
    pendingFiles,
    handleFileChange,
    removeAttachment,
  };
};
